import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import BannerTableData from "Views/Banner/data/BannerTableData";
import { useState, useEffect } from "react";
import ArgonButton from "components/ArgonButton";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import { toast } from "react-toastify";
import api from "utils/api";
import { Link } from "react-router-dom";

function Tables() {
  const [banners, setBanners] = useState([]);
  const [filteredBanners, setFilteredBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedBannerId, setSelectedBannerId] = useState(null);

  useEffect(() => {
    fetchBanners();
  }, []);

  useEffect(() => {
    // Filter banners based on search query
    if (searchQuery.trim() === "") {
      setFilteredBanners(banners);
    } else {
      const filtered = banners.filter(
        (banner) =>
          banner.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          banner.bannerType?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          banner.status?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredBanners(filtered);
    }
  }, [searchQuery, banners]);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await api.get("/banners");
      if (response.data.success) {
        setBanners(response.data.banners || []);
        setFilteredBanners(response.data.banners || []);
      } else {
        toast.error("Failed to fetch banners");
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
      toast.error(error.response?.data?.message || "Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedBannerId(id);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedBannerId(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await api.delete(`/banners/${selectedBannerId}`);
      if (response.data.success) {
        toast.success(response.data.message || "Banner deleted successfully");
        fetchBanners(); // Refresh the list
      } else {
        toast.error(response.data.message || "Failed to delete banner");
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
      toast.error(error.response?.data?.message || "Failed to delete banner");
    } finally {
      handleDeleteClose();
    }
  };

  const { columns, rows } = BannerTableData(filteredBanners, handleDeleteClick, fetchBanners);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3} display="flex" justifyContent="space-between" alignItems="center">
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="medium">
                  Banner List
                </ArgonTypography>
                <ArgonTypography variant="caption" color="text">
                  Total Banners: {filteredBanners.length}
                </ArgonTypography>
              </ArgonBox>
              <ArgonBox display="flex" gap={2} alignItems="center">
                <ArgonInput
                  placeholder="Search banners..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ width: "250px" }}
                />
                <Link to="/banner/addbanner" style={{ textDecoration: "none" }}>
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2 }}
                    style={{ width: "130px" }}
                  >
                    Add Banner
                  </ArgonButton>
                </Link>
              </ArgonBox>
            </ArgonBox>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                  <CircularProgress />
                </ArgonBox>
              ) : filteredBanners.length === 0 ? (
                <ArgonBox p={5} textAlign="center">
                  <ArgonTypography variant="h6" color="text">
                    {searchQuery ? "No banners found matching your search" : "No banners available"}
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>

        {/* Delete Dialog */}
        <DeleteDialog
          open={deleteOpen}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
          title="Are you sure you want to delete this Banner?"
          description="This action cannot be undone."
          cancelText="Cancel"
          confirmText="Delete"
        />
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
